import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import type { AccessibleFarmerProgramme } from "model/useDashboardQueries";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { useMemo, useState } from "react";
import type {
  MilkQualityPerFarmerPeriodData,
  PerFarmerActionsData,
  PerFarmerAssessmentData,
  PerFarmerPcfData,
} from "../hooks/useInsightsDashboardQueries";
import {
  useMilkQualityPerFarmerPeriod,
  usePerFarmerActions,
  usePerFarmerAssessments,
  usePerFarmerPcf,
  useProgrammeInfo,
} from "../hooks/useInsightsDashboardQueries";
import { getSccZone, getTbcZone, getZoneColor } from "../milkQualityThresholds";
import FarmerDetailDialog from "./FarmerDetailDialog";

function TrendIcon({ trend }: { trend: Trend }) {
  const sx = { fontSize: 14, ml: 0.25 };
  if (trend === "up")
    return <TrendingUpIcon sx={{ ...sx, color: COLORS.dahlia[900] }} />;
  if (trend === "down")
    return <TrendingDownIcon sx={{ ...sx, color: COLORS.dahlia[500] }} />;
  return <TrendingFlatIcon sx={{ ...sx, color: "#a1a1aa" }} />;
}

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
});

const GRID_COLS_DAIRY = "minmax(120px, 2fr) 1fr 0.8fr 0.8fr 0.6fr 1.2fr";
const GRID_COLS_DEFAULT = "minmax(120px, 2.5fr) 1.2fr 0.6fr 1.2fr";

function fmt(value: number | undefined | null, maxDecimals = 1): string {
  if (value == null) return "-";
  const num = Number(value);
  if (isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

type Trend = "up" | "down" | "flat";

type FarmerRow = {
  guid: string;
  name: string;
  pcf: number | null;
  hasQuickAssessment: boolean;
  actionsCommitted: number | null;
  actionsOnTrack: number | null;
  actionsCompleted: number | null;
  latestScc: number | null;
  latestTbc: number | null;
  sccTrend: Trend;
  tbcTrend: Trend;
};

function computeTrend(latest: number, previous: number | undefined): Trend {
  if (previous === undefined) return "flat";
  if (latest > previous * 1.05) return "up";
  if (latest < previous * 0.95) return "down";
  return "flat";
}

type MilkPerFarmer = {
  scc: number | null;
  tbc: number | null;
  sccTrend: Trend;
  tbcTrend: Trend;
};

function getLatestMilkPerFarmer(
  data: MilkQualityPerFarmerPeriodData[] | undefined
): Map<string, MilkPerFarmer> {
  const byFarmer = new Map<string, MilkQualityPerFarmerPeriodData[]>();
  for (const r of data ?? []) {
    const uid = r["MilkQuality.userGuid"];
    if (!byFarmer.has(uid)) byFarmer.set(uid, []);
    byFarmer.get(uid)?.push(r);
  }

  const result = new Map<string, MilkPerFarmer>();
  for (const [uid, records] of byFarmer) {
    const sorted = records.sort((a, b) =>
      String(a["MilkQuality.periodStart"]).localeCompare(
        String(b["MilkQuality.periodStart"])
      )
    );
    const latest = sorted[sorted.length - 1];
    const previous = sorted.length > 1 ? sorted[sorted.length - 2] : undefined;

    const scc = Number(latest["MilkQuality.somaticCellCount"]);
    const tbc = Number(latest["MilkQuality.totalBacterialCount"]);

    result.set(uid, {
      scc: scc === 0 ? null : scc,
      tbc: tbc === 0 ? null : tbc,
      sccTrend:
        scc === 0
          ? "flat"
          : computeTrend(
              scc,
              previous
                ? Number(previous["MilkQuality.somaticCellCount"])
                : undefined
            ),
      tbcTrend:
        tbc === 0
          ? "flat"
          : computeTrend(
              tbc,
              previous
                ? Number(previous["MilkQuality.totalBacterialCount"])
                : undefined
            ),
    });
  }
  return result;
}

function buildFarmerRows(
  farmers: AccessibleFarmerProgramme[],
  actionsData: PerFarmerActionsData[] | undefined,
  assessmentData: PerFarmerAssessmentData[] | undefined,
  pcfData: PerFarmerPcfData[] | undefined,
  milkData: MilkQualityPerFarmerPeriodData[] | undefined
): FarmerRow[] {
  const actionsMap = new Map<string, PerFarmerActionsData>();
  (actionsData ?? []).forEach((r) => {
    actionsMap.set(r["SubmittedActions.userGuid"], r);
  });

  const assessmentMap = new Map<string, boolean>();
  (assessmentData ?? []).forEach((r) => {
    const count = Number(r["AssessmentSubmitted.count"]);
    assessmentMap.set(r["AssessmentSubmitted.userGuid"], count > 0);
  });

  const pcfMap = new Map<string, number>();
  (pcfData ?? []).forEach((r) => {
    pcfMap.set(r["Emissions.userGuid"], Number(r["Emissions.avgValue"]));
  });

  const milkMap = getLatestMilkPerFarmer(milkData);

  return farmers.map((f) => {
    const guid = f.userGuid;
    const actions = actionsMap.get(guid);
    const uniqueActions = actions
      ? Number(actions["SubmittedActions.uniqueActionCount"])
      : null;
    const milk = milkMap.get(guid);
    return {
      guid,
      name: f.userName || f.userEmail,
      pcf: pcfMap.get(guid) ?? null,
      hasQuickAssessment: assessmentMap.get(guid) ?? false,
      actionsCommitted: uniqueActions,
      actionsOnTrack: null,
      actionsCompleted: null,
      latestScc: milk?.scc ?? null,
      latestTbc: milk?.tbc ?? null,
      sccTrend: milk?.sccTrend ?? "flat",
      tbcTrend: milk?.tbcTrend ?? "flat",
    };
  });
}

export function FarmerTable() {
  const { data: farmersData, isLoading: farmersLoading } =
    useAccessibleFarmerProgrammes();
  const { data: actionsData, isLoading: actionsLoading } =
    usePerFarmerActions();
  const { data: assessmentData, isLoading: assessmentLoading } =
    usePerFarmerAssessments();
  const { data: pcfData, isLoading: pcfLoading } = usePerFarmerPcf();
  const { data: milkData, isLoading: milkLoading } =
    useMilkQualityPerFarmerPeriod();
  const { data: programmeInfo } = useProgrammeInfo();

  const isDairy =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] === "DAIRY";

  const farmers = farmersData ?? [];

  const [search, setSearch] = useState("");
  const [selectedFarmer, setSelectedFarmer] = useState<{
    guid: string;
    name: string;
  } | null>(null);

  const isLoading =
    farmersLoading ||
    actionsLoading ||
    assessmentLoading ||
    pcfLoading ||
    milkLoading;

  const rows = useMemo(
    () =>
      buildFarmerRows(
        farmers,
        actionsData?.data,
        assessmentData?.data,
        pcfData?.data,
        milkData?.data
      ),
    [
      farmers,
      actionsData?.data,
      assessmentData?.data,
      pcfData?.data,
      milkData?.data,
    ]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(term));
  }, [rows, search]);

  const aggregates = useMemo(() => {
    const totalPcf = filteredRows.reduce((sum, r) => sum + (r.pcf ?? 0), 0);
    const farmersWithPcf = filteredRows.filter((r) => r.pcf != null).length;
    const avgPcf = farmersWithPcf > 0 ? totalPcf / farmersWithPcf : null;
    const totalActionsCommitted = filteredRows.reduce(
      (sum, r) => sum + (r.actionsCommitted ?? 0),
      0
    );
    const totalActionsOnTrack = filteredRows.reduce(
      (sum, r) => sum + (r.actionsOnTrack ?? 0),
      0
    );
    const totalActionsCompleted = filteredRows.reduce(
      (sum, r) => sum + (r.actionsCompleted ?? 0),
      0
    );
    const farmersWithAssessment = filteredRows.filter(
      (r) => r.hasQuickAssessment
    ).length;
    return {
      count: filteredRows.length,
      avgPcf,
      totalActionsCommitted,
      totalActionsOnTrack,
      totalActionsCompleted,
      farmersWithAssessment,
    };
  }, [filteredRows]);

  if (!isLoading && farmers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        No farmers found in your programme.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search farmers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#a1a1aa", fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: 360,
            "& .MuiOutlinedInput-root": {
              borderRadius: `${BORDER_RADIUS.small}px`,
              backgroundColor: "white",
            },
          }}
        />
      </Box>

      <Box>
        <Card>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isDairy
                ? GRID_COLS_DAIRY
                : GRID_COLS_DEFAULT,
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#71717a" }}
            >
              Farmer
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
            >
              PCF
            </Typography>
            {isDairy && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
              >
                SCC
              </Typography>
            )}
            {isDairy && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
              >
                TBC
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
            >
              Baseline
            </Typography>
            <Box
              sx={{
                textAlign: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#71717a" }}
                >
                  Actions
                </Typography>
                <Tooltip
                  title="committed / on track / done"
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: "#a1a1aa",
                      cursor: "help",
                      display: { xs: "block", sm: "none" },
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#a1a1aa",
                  fontSize: "10px",
                  display: { xs: "none", sm: "block" },
                }}
              >
                committed / on track / done
              </Typography>
            </Box>
          </Box>

          {isLoading
            ? [1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isDairy
                      ? GRID_COLS_DAIRY
                      : GRID_COLS_DEFAULT,
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #f4f4f5",
                  }}
                >
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton
                    variant="text"
                    width={60}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                  {isDairy && (
                    <Skeleton
                      variant="text"
                      width={40}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  )}
                  {isDairy && (
                    <Skeleton
                      variant="text"
                      width={40}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  )}
                  <Skeleton
                    variant="text"
                    width={30}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                </Box>
              ))
            : filteredRows.map((row) => (
                <Box
                  key={row.guid}
                  onClick={() =>
                    setSelectedFarmer({ guid: row.guid, name: row.name })
                  }
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isDairy
                      ? GRID_COLS_DAIRY
                      : GRID_COLS_DEFAULT,
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #f4f4f5",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: COLORS.dahlia[50] },
                    "&:last-child": { borderBottom: "none" },
                    transition: "background-color 0.15s",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#18181b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#3f3f46",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.pcf != null ? (
                      <>
                        {fmt(row.pcf)}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "#a1a1aa", ml: 0.5 }}
                        >
                          kg CO₂e/kg
                        </Typography>
                      </>
                    ) : (
                      "-"
                    )}
                  </Typography>
                  {isDairy && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.25,
                      }}
                    >
                      {row.latestScc != null ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: `${getZoneColor(
                                getSccZone(row.latestScc)
                              )}18`,
                              color: getZoneColor(getSccZone(row.latestScc)),
                            }}
                          >
                            {fmt(row.latestScc, 0)}
                          </Typography>
                          <TrendIcon trend={row.sccTrend} />
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#a1a1aa" }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  )}
                  {isDairy && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.25,
                      }}
                    >
                      {row.latestTbc != null ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: `${getZoneColor(
                                getTbcZone(row.latestTbc)
                              )}18`,
                              color: getZoneColor(getTbcZone(row.latestTbc)),
                            }}
                          >
                            {fmt(row.latestTbc, 0)}
                          </Typography>
                          <TrendIcon trend={row.tbcTrend} />
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#a1a1aa" }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: "#3f3f46", textAlign: "right" }}
                  >
                    {row.hasQuickAssessment ? "Yes" : "No"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#3f3f46",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmt(row.actionsCommitted, 0)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#a1a1aa", mx: 0.25 }}
                    >
                      /
                    </Typography>
                    {fmt(row.actionsOnTrack, 0)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#a1a1aa", mx: 0.25 }}
                    >
                      /
                    </Typography>
                    {fmt(row.actionsCompleted, 0)}
                  </Typography>
                </Box>
              ))}

          {!isLoading && filteredRows.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No farmers match your search.
              </Typography>
            </Box>
          )}

          {!isLoading && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isDairy
                  ? GRID_COLS_DAIRY
                  : GRID_COLS_DEFAULT,
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: "2px solid #e5e5e5",
                backgroundColor: "#f5f5f5",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#18181b" }}
              >
                Total ({aggregates.count})
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#3f3f46", textAlign: "right" }}
              >
                {aggregates.avgPcf != null ? (
                  <>
                    {fmt(aggregates.avgPcf)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#a1a1aa", ml: 0.5 }}
                    >
                      avg
                    </Typography>
                  </>
                ) : (
                  "-"
                )}
              </Typography>
              {isDairy && (
                <Typography
                  variant="body2"
                  sx={{ color: "#a1a1aa", textAlign: "right" }}
                >
                  -
                </Typography>
              )}
              {isDairy && (
                <Typography
                  variant="body2"
                  sx={{ color: "#a1a1aa", textAlign: "right" }}
                >
                  -
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{ color: "#3f3f46", textAlign: "right" }}
              >
                {aggregates.farmersWithAssessment}/{aggregates.count}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#3f3f46",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {fmt(aggregates.totalActionsCommitted, 0)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#a1a1aa", mx: 0.25 }}
                >
                  /
                </Typography>
                {fmt(aggregates.totalActionsOnTrack, 0)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#a1a1aa", mx: 0.25 }}
                >
                  /
                </Typography>
                {fmt(aggregates.totalActionsCompleted, 0)}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      <FarmerDetailDialog
        open={selectedFarmer !== null}
        farmerGuid={selectedFarmer?.guid ?? null}
        farmerName={selectedFarmer?.name ?? null}
        onClose={() => setSelectedFarmer(null)}
      />
    </>
  );
}
